import { useEffect, useState } from "react";
import { api, notifyError } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
  Plus,
  Building2,
  Trash2,
  Users,
  PackageOpen,
  Loader2,
  Mail,
  Phone,
  KeyRound,
  Pencil,
  UserCog,
  Eye,
  EyeOff,
} from "lucide-react";
import { toast } from "sonner";

const empty = {
  name: "",
  contact_name: "",
  contact_phone: "",
  owner_email: "",
  owner_password: "",
  owner_name: "",
};

export default function MasterTenants() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [credsShown, setCredsShown] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({
    name: "",
    contact_name: "",
    contact_phone: "",
    owner_email: "",
    owner_password: "",
    owner_name: "",
    owner_phone: "",
    owner_address: "",
    owner_city: "",
    owner_district: "",
    owner_invoice_type: "individual",
    owner_invoice_title: "",
    owner_tax_no: "",
    owner_tax_office: "",
    owner_invoice_address: "",
    owner_billing_email: "",
  });
  const [editSaving, setEditSaving] = useState(false);
  
  // Staff management states
  const [staffTenant, setStaffTenant] = useState(null);
  const [staffList, setStaffList] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [staffEditTarget, setStaffEditTarget] = useState(null);
  const [staffEditForm, setStaffEditForm] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    role: "personel",
    address: "",
    city: "",
    district: "",
    invoice_type: "individual",
    invoice_title: "",
    tax_no: "",
    tax_office: "",
    invoice_address: "",
    billing_email: "",
  });
  const [staffEditSaving, setStaffEditSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/master/tenants");
      setList(data);
    } catch (e) {
      notifyError(e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/master/tenants", form);
      toast.success(`İşletme "${form.name}" oluşturuldu`);
      setCredsShown({
        tenant: form.name,
        email: form.owner_email,
        password: form.owner_password,
      });
      setForm(empty);
      setOpen(false);
      await load();
    } catch (err) {
      notifyError(err);
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (t) => {
    try {
      const newStatus = t.status === "active" ? "suspended" : "active";
      await api.put(`/master/tenants/${t.id}`, { status: newStatus });
      toast.success(newStatus === "active" ? "İşletme aktifleştirildi" : "İşletme askıya alındı");
      await load();
    } catch (err) {
      notifyError(err);
    }
  };

  const openEdit = (t) => {
    setEditTarget(t);
    setEditForm({
      name: t.name || "",
      contact_name: t.contact_name || "",
      contact_phone: t.contact_phone || "",
      owner_email: t.owner_email || "",
      owner_password: "",
      owner_name: t.owner_name || "",
      owner_phone: t.owner_phone || t.contact_phone || "",
      owner_address: t.owner_address || "",
      owner_city: t.owner_city || "",
      owner_district: t.owner_district || "",
      owner_invoice_type: t.owner_invoice_type || "individual",
      owner_invoice_title: t.owner_invoice_title || "",
      owner_tax_no: t.owner_tax_no || "",
      owner_tax_office: t.owner_tax_office || "",
      owner_invoice_address: t.owner_invoice_address || "",
      owner_billing_email: t.owner_billing_email || "",
    });
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editTarget) return;
    const payload = {};
    if (editForm.name !== (editTarget.name || "")) payload.name = editForm.name;
    if (editForm.contact_name !== (editTarget.contact_name || "")) payload.contact_name = editForm.contact_name;
    if (editForm.contact_phone !== (editTarget.contact_phone || "")) payload.contact_phone = editForm.contact_phone;
    if (
      editForm.owner_email &&
      editForm.owner_email.toLowerCase() !== (editTarget.owner_email || "").toLowerCase()
    ) {
      payload.owner_email = editForm.owner_email.trim();
    }
    if (editForm.owner_password) {
      if (editForm.owner_password.length < 6) {
        toast.error("Şifre en az 6 karakter olmalıdır");
        return;
      }
      payload.owner_password = editForm.owner_password;
    }
    const ownerDiffFields = [
      "owner_name",
      "owner_phone",
      "owner_address",
      "owner_city",
      "owner_district",
      "owner_invoice_type",
      "owner_invoice_title",
      "owner_tax_no",
      "owner_tax_office",
      "owner_invoice_address",
      "owner_billing_email",
    ];
    for (const f of ownerDiffFields) {
      const current = editForm[f] ?? "";
      const prev = editTarget[f] ?? "";
      if (current !== prev) {
        payload[f] = current === "" ? null : current;
      }
    }
    if (Object.keys(payload).length === 0) {
      toast.info("Değişiklik yapmadınız");
      return;
    }
    setEditSaving(true);
    try {
      await api.put(`/master/tenants/${editTarget.id}`, payload);
      toast.success("İşletme bilgileri güncellendi");
      if (payload.owner_password) {
        setCredsShown({
          tenant: editForm.name || editTarget.name,
          email: payload.owner_email || editTarget.owner_email,
          password: payload.owner_password,
        });
      }
      setEditTarget(null);
      await load();
    } catch (err) {
      notifyError(err);
    } finally {
      setEditSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/master/tenants/${deleteId}`);
      toast.success("İşletme silindi");
      setDeleteId(null);
      await load();
    } catch (err) {
      notifyError(err);
    }
  };

  const loadStaff = async (tenant) => {
    setStaffTenant(tenant);
    setStaffLoading(true);
    try {
      const { data } = await api.get(`/master/tenants/${tenant.id}/staff`);
      setStaffList(data);
    } catch (e) {
      notifyError(e);
    } finally {
      setStaffLoading(false);
    }
  };

  const openStaffEdit = (staff) => {
    setStaffEditTarget(staff);
    setStaffEditForm({
      name: staff.name || "",
      email: staff.email || "",
      password: "",
      phone: staff.phone || "",
      role: staff.role || "personel",
      address: staff.address || "",
      city: staff.city || "",
      district: staff.district || "",
      invoice_type: staff.invoice_type || "individual",
      invoice_title: staff.invoice_title || "",
      tax_no: staff.tax_no || "",
      tax_office: staff.tax_office || "",
      invoice_address: staff.invoice_address || "",
      billing_email: staff.billing_email || "",
    });
  };

  const submitStaffEdit = async (e) => {
    e.preventDefault();
    if (!staffTenant || !staffEditTarget) return;

    // Check if trying to remove the last owner
    if (staffEditForm.role !== "owner" && staffEditTarget.role === "owner") {
      const ownerCount = staffList.filter(s => s.role === "owner").length;
      if (ownerCount <= 1) {
        toast.error("Farklı bir yönetici bulunmadığı için yetki değiştirilemez.");
        return;
      }
    }

    const payload = {};
    if (staffEditForm.name !== (staffEditTarget.name || "")) payload.name = staffEditForm.name;
    if (staffEditForm.email.toLowerCase() !== (staffEditTarget.email || "").toLowerCase()) {
      payload.email = staffEditForm.email.trim();
    }
    if (staffEditForm.password) {
      if (staffEditForm.password.length < 6) {
        toast.error("Şifre en az 6 karakter olmalıdır");
        return;
      }
      payload.password = staffEditForm.password;
    }
    if (staffEditForm.role !== (staffEditTarget.role || "personel")) payload.role = staffEditForm.role;
    
    const staffDiffFields = [
      "phone", "address", "city", "district",
      "invoice_title", "tax_no", "tax_office", "invoice_address",
    ];
    for (const f of staffDiffFields) {
      const current = staffEditForm[f] ?? "";
      const prev = staffEditTarget[f] ?? "";
      if (current !== prev) {
        payload[f] = current === "" ? null : current;
      }
    }
    if (staffEditForm.invoice_type !== (staffEditTarget.invoice_type || "individual")) {
      payload.invoice_type = staffEditForm.invoice_type;
    }
    if (staffEditForm.billing_email !== (staffEditTarget.billing_email || "")) {
      payload.billing_email = staffEditForm.billing_email.trim().toLowerCase() || null;
    }

    if (Object.keys(payload).length === 0) {
      toast.info("Değişiklik yapmadınız");
      return;
    }

    setStaffEditSaving(true);
    try {
      await api.put(`/master/tenants/${staffTenant.id}/staff/${staffEditTarget.id}`, payload);
      toast.success("Personel güncellendi");
      setStaffEditTarget(null);
      await loadStaff(staffTenant);
    } catch (err) {
      notifyError(err);
    } finally {
      setStaffEditSaving(false);
    }
  };

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto space-y-6" data-testid="master-tenants-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-eyebrow text-amber-600">Yönetim</div>
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-2">İşletmeler</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Toplam {list.length} işletme yönetiliyor.
          </p>
        </div>
        <Button
          onClick={() => { setForm(empty); setOpen(true); }}
          className="rounded-sm bg-foreground text-background hover:bg-foreground/90"
          data-testid="add-tenant-btn"
        >
          <Plus className="h-4 w-4 mr-2" /> Yeni İşletme
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-16"><Loader2 className="h-6 w-6 animate-spin inline" /></div>
      ) : list.length === 0 ? (
        <Card className="p-12 text-center rounded-sm">
          <Building2 className="h-10 w-10 mx-auto text-muted-foreground" />
          <div className="font-display font-bold text-lg mt-3">Henüz işletme yok</div>
          <p className="text-sm text-muted-foreground mt-2">İlk lastik oteli işletmenizi oluşturun.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {list.map((t) => (
            <Card key={t.id} className={`rounded-sm p-5 border ${t.status === "active" ? "border-border" : "border-destructive/40 bg-destructive/5"}`} data-testid={`tenant-card-${t.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Building2 className="h-4 w-4 text-amber-600" />
                    <Badge className={`rounded-sm text-xs ${t.status === "active" ? "bg-green-600 hover:bg-green-600" : "bg-destructive hover:bg-destructive"} text-white`}>
                      {t.status === "active" ? "AKTİF" : "ASKIDA"}
                    </Badge>
                  </div>
                  <div className="font-display font-bold text-xl tracking-tight truncate">{t.name}</div>
                  <div className="mt-2 space-y-1 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1.5"><Mail className="h-3 w-3" /> {t.owner_email}</div>
                    {t.contact_phone && <div className="flex items-center gap-1.5"><Phone className="h-3 w-3" /> {t.contact_phone}</div>}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-border">
                <Stat label="Kullanıcı" value={t.user_count} icon={Users} />
                <Stat label="Müşteri" value={t.customer_count} icon={Users} />
                <Stat label="Emanet" value={t.storage_count} icon={PackageOpen} />
              </div>

              <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={t.status === "active"}
                    onCheckedChange={() => toggleStatus(t)}
                    data-testid={`toggle-tenant-${t.id}`}
                  />
                  <span className="text-xs text-muted-foreground">
                    {t.status === "active" ? "Aktif" : "Askıda"}
                  </span>
                </div>
              <div className="flex items-center gap-1">
                  <button
                    onClick={() => loadStaff(t)}
                    className="p-2 hover:bg-blue-500/10 hover:text-blue-600 rounded-sm text-muted-foreground"
                    data-testid={`staff-tenant-${t.id}`}
                    aria-label="Personel"
                    title="Personel"
                  >
                    <Users className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => openEdit(t)}
                    className="p-2 hover:bg-amber-500/10 hover:text-amber-600 rounded-sm text-muted-foreground"
                    data-testid={`edit-tenant-${t.id}`}
                    aria-label="Düzenle"
                    title="Düzenle"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setDeleteId(t.id)}
                    className="p-2 hover:bg-destructive/10 hover:text-destructive rounded-sm text-muted-foreground"
                    data-testid={`delete-tenant-${t.id}`}
                    aria-label="Sil"
                    title="Sil"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Yeni İşletme Oluştur</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="tenant-form">
            <div className="space-y-1.5">
              <Label className="label-eyebrow">İşletme Adı *</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" data-testid="tenant-name" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="label-eyebrow">İletişim Adı</Label>
                <Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="rounded-sm" />
              </div>
              <div className="space-y-1.5">
                <Label className="label-eyebrow">Telefon</Label>
                <Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} className="rounded-sm" />
              </div>
            </div>

            <div className="border-t border-border pt-4">
              <div className="label-eyebrow mb-3">Yönetici (Owner) Hesabı</div>
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="label-eyebrow">Ad Soyad</Label>
                  <Input value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} className="rounded-sm" data-testid="tenant-owner-name" />
                </div>
                <div className="space-y-1.5">
                  <Label className="label-eyebrow">E-posta *</Label>
                  <Input required type="email" value={form.owner_email} onChange={(e) => setForm({ ...form, owner_email: e.target.value })} className="rounded-sm" data-testid="tenant-owner-email" />
                </div>
                <div className="space-y-1.5">
                  <Label className="label-eyebrow">Geçici Şifre *</Label>
                  <Input required minLength={6} type="text" value={form.owner_password} onChange={(e) => setForm({ ...form, owner_password: e.target.value })} className="rounded-sm font-mono" placeholder="En az 6 karakter" data-testid="tenant-owner-password" />
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-sm">İptal</Button>
              <Button type="submit" disabled={saving} className="rounded-sm bg-foreground text-background hover:bg-foreground/90" data-testid="tenant-submit">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Oluştur"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Credentials shown after create */}
      <Dialog open={!!credsShown} onOpenChange={(o) => !o && setCredsShown(null)}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-amber-600" /> Giriş Bilgileri
            </DialogTitle>
          </DialogHeader>
          {credsShown && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                <strong>{credsShown.tenant}</strong> işletmesi için yönetici hesabı oluşturuldu. Bu bilgileri işletme sahibine iletin:
              </p>
              <div className="bg-foreground text-background p-4 rounded-sm font-mono text-sm space-y-1">
                <div><span className="text-amber-400">E-posta:</span> {credsShown.email}</div>
                <div><span className="text-amber-400">Şifre:</span> {credsShown.password}</div>
              </div>
              <p className="text-xs text-muted-foreground">
                Bu pencere kapandıktan sonra şifre tekrar gösterilemez.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setCredsShown(null)} className="rounded-sm bg-foreground text-background hover:bg-foreground/90">Tamam</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit tenant dialog */}
      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">İşletme Bilgilerini Düzenle</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitEdit} className="space-y-5" data-testid="tenant-edit-form">
            <div>
              <div className="label-eyebrow text-amber-600 mb-2">İşletme</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="label-eyebrow">İşletme Adı *</Label>
                  <Input
                    required
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    className="rounded-sm"
                    data-testid="edit-tenant-name"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="label-eyebrow">İletişim Adı</Label>
                  <Input
                    value={editForm.contact_name}
                    onChange={(e) => setEditForm({ ...editForm, contact_name: e.target.value })}
                    className="rounded-sm"
                    data-testid="edit-tenant-contact-name"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="label-eyebrow">İşletme Telefonu</Label>
                  <Input
                    value={editForm.contact_phone}
                    onChange={(e) => setEditForm({ ...editForm, contact_phone: e.target.value })}
                    className="rounded-sm"
                    data-testid="edit-tenant-phone"
                  />
                </div>
              </div>
            </div>

            <div className="border-t border-border pt-4">
              <div className="label-eyebrow text-amber-600 mb-2">Yönetici (Owner) Hesabı</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="label-eyebrow">Ad Soyad</Label>
                  <Input
                    value={editForm.owner_name}
                    onChange={(e) => setEditForm({ ...editForm, owner_name: e.target.value })}
                    className="rounded-sm"
                    data-testid="edit-tenant-owner-name"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="label-eyebrow">E-posta</Label>
                  <Input
                    type="email"
                    value={editForm.owner_email}
                    onChange={(e) => setEditForm({ ...editForm, owner_email: e.target.value })}
                    className="rounded-sm"
                    data-testid="edit-tenant-owner-email"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="label-eyebrow">Cep Telefonu</Label>
                  <Input
                    type="tel"
                    value={editForm.owner_phone}
                    onChange={(e) => setEditForm({ ...editForm, owner_phone: e.target.value })}
                    placeholder="0532 000 00 00"
                    className="rounded-sm font-mono"
                    data-testid="edit-tenant-owner-phone"
                  />
                  <p className="text-[11px] text-muted-foreground">
                    PayTR canlı mod için zorunlu. Rakamlardan oluşan en az 10 hane.
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label className="label-eyebrow">Yeni Şifre (opsiyonel)</Label>
                  <Input
                    type="text"
                    value={editForm.owner_password}
                    onChange={(e) => setEditForm({ ...editForm, owner_password: e.target.value })}
                    className="rounded-sm font-mono"
                    placeholder="En az 6 karakter"
                    data-testid="edit-tenant-owner-password"
                  />
                  <p className="text-[11px] text-muted-foreground">
                    Boş bırakılırsa mevcut şifre korunur.
                  </p>
                </div>
              </div>
            </div>

            <div className="border-t border-border pt-4">
              <div className="label-eyebrow text-amber-600 mb-2">Adres</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="label-eyebrow">Adres</Label>
                  <Textarea
                    rows={2}
                    value={editForm.owner_address}
                    onChange={(e) => setEditForm({ ...editForm, owner_address: e.target.value })}
                    placeholder="Sokak, mahalle, bina no, daire"
                    className="rounded-sm"
                    data-testid="edit-tenant-owner-address"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="label-eyebrow">İlçe</Label>
                  <Input
                    value={editForm.owner_district}
                    onChange={(e) => setEditForm({ ...editForm, owner_district: e.target.value })}
                    className="rounded-sm"
                    data-testid="edit-tenant-owner-district"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="label-eyebrow">İl</Label>
                  <Input
                    value={editForm.owner_city}
                    onChange={(e) => setEditForm({ ...editForm, owner_city: e.target.value })}
                    className="rounded-sm"
                    data-testid="edit-tenant-owner-city"
                  />
                </div>
              </div>
            </div>

            <div className="border-t border-border pt-4">
              <div className="label-eyebrow text-amber-600 mb-2">Fatura Bilgileri</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="label-eyebrow">Fatura Tipi</Label>
                  <Select
                    value={editForm.owner_invoice_type}
                    onValueChange={(v) => setEditForm({ ...editForm, owner_invoice_type: v })}
                  >
                    <SelectTrigger className="rounded-sm" data-testid="edit-tenant-invoice-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="individual">Bireysel</SelectItem>
                      <SelectItem value="company">Kurumsal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="label-eyebrow">Fatura E-postası</Label>
                  <Input
                    type="email"
                    value={editForm.owner_billing_email}
                    onChange={(e) => setEditForm({ ...editForm, owner_billing_email: e.target.value })}
                    placeholder="muhasebe@firma.com"
                    className="rounded-sm"
                    data-testid="edit-tenant-billing-email"
                  />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="label-eyebrow">
                    {editForm.owner_invoice_type === "company" ? "Ünvan" : "Ad Soyad (Fatura)"}
                  </Label>
                  <Input
                    value={editForm.owner_invoice_title}
                    onChange={(e) => setEditForm({ ...editForm, owner_invoice_title: e.target.value })}
                    placeholder={
                      editForm.owner_invoice_type === "company"
                        ? "Firma ünvanı"
                        : "Faturada görünecek isim"
                    }
                    className="rounded-sm"
                    data-testid="edit-tenant-invoice-title"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="label-eyebrow">
                    {editForm.owner_invoice_type === "company" ? "Vergi No" : "TC Kimlik No"}
                  </Label>
                  <Input
                    value={editForm.owner_tax_no}
                    onChange={(e) => setEditForm({ ...editForm, owner_tax_no: e.target.value })}
                    className="rounded-sm font-mono"
                    data-testid="edit-tenant-tax-no"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="label-eyebrow">
                    Vergi Dairesi {editForm.owner_invoice_type === "company" ? "" : "(opsiyonel)"}
                  </Label>
                  <Input
                    value={editForm.owner_tax_office}
                    onChange={(e) => setEditForm({ ...editForm, owner_tax_office: e.target.value })}
                    className="rounded-sm"
                    data-testid="edit-tenant-tax-office"
                  />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="label-eyebrow">Fatura Adresi (Adresten farklıysa)</Label>
                  <Textarea
                    rows={2}
                    value={editForm.owner_invoice_address}
                    onChange={(e) => setEditForm({ ...editForm, owner_invoice_address: e.target.value })}
                    placeholder="Boş bırakılırsa yukarıdaki adres kullanılır"
                    className="rounded-sm"
                    data-testid="edit-tenant-invoice-address"
                  />
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditTarget(null)}
                className="rounded-sm"
              >
                İptal
              </Button>
              <Button
                type="submit"
                disabled={editSaving}
                className="rounded-sm bg-foreground text-background hover:bg-foreground/90"
                data-testid="edit-tenant-submit"
              >
                {editSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kaydet"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display tracking-tight">İşletme silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>
              Bu işlem geri alınamaz. İşletmenin tüm verileri (kullanıcılar, müşteriler, emanetler, ödemeler) kalıcı olarak silinecektir.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="rounded-sm bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-tenant">
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Staff management dialog */}
      <Dialog open={!!staffTenant} onOpenChange={(o) => !o && setStaffTenant(null)}>
        <DialogContent className="rounded-sm max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              {staffTenant?.name} — Personel
            </DialogTitle>
          </DialogHeader>
          
          {staffLoading ? (
            <div className="text-center py-8"><Loader2 className="h-6 w-6 animate-spin inline" /></div>
          ) : staffList.length === 0 ? (
            <Card className="p-12 text-center rounded-sm">
              <UserCog className="h-10 w-10 mx-auto text-muted-foreground" />
              <div className="font-display font-bold text-lg mt-3">Personel yok</div>
            </Card>
          ) : (
            <Card className="rounded-sm border-border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-foreground text-background">
                    <tr>
                      <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">Ad</th>
                      <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">E-posta</th>
                      <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">Telefon</th>
                      <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">Rol</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {staffList.map((s) => (
                      <tr key={s.id} data-testid={`master-staff-row-${s.id}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="h-8 w-8 rounded-sm bg-muted flex items-center justify-center">
                              <UserCog className="h-4 w-4" />
                            </div>
                            <div className="font-semibold">{s.name}</div>
                          </div>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs">
                          <div className="flex items-center gap-1.5"><Mail className="h-3 w-3 text-muted-foreground" />{s.email}</div>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                          {s.phone || "—"}
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={`rounded-sm uppercase tracking-wider text-xs ${s.role === "owner" ? "bg-amber-500 text-white hover:bg-amber-500" : "bg-secondary text-secondary-foreground hover:bg-secondary"}`}>
                            {s.role === "owner" ? "Yönetici" : "Personel"}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => openStaffEdit(s)}
                            className="p-2 hover:bg-muted rounded-sm"
                            data-testid={`edit-master-staff-${s.id}`}
                            aria-label="Düzenle"
                            title="Düzenle"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </DialogContent>
      </Dialog>

      {/* Staff edit dialog */}
      <Dialog open={!!staffEditTarget} onOpenChange={(o) => !o && setStaffEditTarget(null)}>
        <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Personeli Düzenle</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitStaffEdit} className="space-y-5" data-testid="master-staff-edit-form">
            <div>
              <div className="label-eyebrow text-amber-600 mb-2">Temel Bilgiler</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="label-eyebrow">Ad Soyad *</Label>
                  <Input required value={staffEditForm.name} onChange={(e) => setStaffEditForm({ ...staffEditForm, name: e.target.value })} className="rounded-sm" data-testid="master-staff-name" />
                </div>
                <div className="space-y-1.5">
                  <Label className="label-eyebrow">E-posta *</Label>
                  <Input required type="email" value={staffEditForm.email} onChange={(e) => setStaffEditForm({ ...staffEditForm, email: e.target.value })} className="rounded-sm" data-testid="master-staff-email" />
                </div>
                <div className="space-y-1.5">
                  <Label className="label-eyebrow">Yeni Şifre (opsiyonel)</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={staffEditForm.password}
                      onChange={(e) => setStaffEditForm({ ...staffEditForm, password: e.target.value })}
                      className="rounded-sm font-mono pr-10"
                      placeholder="Değiştirmek için yazın"
                      data-testid="master-staff-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-muted rounded-sm text-muted-foreground"
                      aria-label={showPassword ? "Şifreyi gizle" : "Şifreyi göster"}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <p className="text-[11px] text-muted-foreground">Boş bırakılırsa mevcut şifre korunur. En az 6 karakter.</p>
                </div>
                <div className="space-y-1.5">
                  <Label className="label-eyebrow">Cep Telefonu</Label>
                  <Input
                    type="tel"
                    value={staffEditForm.phone}
                    onChange={(e) => setStaffEditForm({ ...staffEditForm, phone: e.target.value })}
                    placeholder="0532 000 00 00"
                    className="rounded-sm font-mono"
                    data-testid="master-staff-phone"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="label-eyebrow">Rol</Label>
                  <Select value={staffEditForm.role} onValueChange={(v) => setStaffEditForm({ ...staffEditForm, role: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="master-staff-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="personel">Personel</SelectItem>
                      <SelectItem value="owner">Yönetici (Owner)</SelectItem>
                    </SelectContent>
                  </Select>
                  {staffEditForm.role === "owner" && staffEditTarget?.role !== "owner" && (
                    <p className="text-[11px] text-blue-600 font-semibold">
                      ℹ️ Bu personel yönetici haklarına sahip olacak.
                    </p>
                  )}
                  {staffEditForm.role !== "owner" && staffEditTarget?.role === "owner" && (
                    <p className="text-[11px] text-amber-600 font-semibold">
                      ⚠️ Bu yönetici personel rolüne indirilecek.
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="border-t border-border pt-4">
              <div className="label-eyebrow text-amber-600 mb-2">Adres</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="label-eyebrow">Adres</Label>
                  <Textarea
                    rows={2}
                    value={staffEditForm.address}
                    onChange={(e) => setStaffEditForm({ ...staffEditForm, address: e.target.value })}
                    placeholder="Sokak, mahalle, bina no, daire"
                    className="rounded-sm"
                    data-testid="master-staff-address"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="label-eyebrow">İlçe</Label>
                  <Input
                    value={staffEditForm.district}
                    onChange={(e) => setStaffEditForm({ ...staffEditForm, district: e.target.value })}
                    className="rounded-sm"
                    data-testid="master-staff-district"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="label-eyebrow">İl</Label>
                  <Input
                    value={staffEditForm.city}
                    onChange={(e) => setStaffEditForm({ ...staffEditForm, city: e.target.value })}
                    className="rounded-sm"
                    data-testid="master-staff-city"
                  />
                </div>
              </div>
            </div>

            <div className="border-t border-border pt-4">
              <div className="label-eyebrow text-amber-600 mb-2">Fatura Bilgileri</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="label-eyebrow">Fatura Tipi</Label>
                  <Select value={staffEditForm.invoice_type} onValueChange={(v) => setStaffEditForm({ ...staffEditForm, invoice_type: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="master-staff-invoice-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="individual">Bireysel</SelectItem>
                      <SelectItem value="company">Kurumsal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="label-eyebrow">Fatura E-postası</Label>
                  <Input
                    type="email"
                    value={staffEditForm.billing_email}
                    onChange={(e) => setStaffEditForm({ ...staffEditForm, billing_email: e.target.value })}
                    placeholder="muhasebe@firma.com"
                    className="rounded-sm"
                    data-testid="master-staff-billing-email"
                  />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="label-eyebrow">
                    {staffEditForm.invoice_type === "company" ? "Ünvan" : "Ad Soyad (Fatura)"}
                  </Label>
                  <Input
                    value={staffEditForm.invoice_title}
                    onChange={(e) => setStaffEditForm({ ...staffEditForm, invoice_title: e.target.value })}
                    placeholder={staffEditForm.invoice_type === "company" ? "Firma ünvanı" : "Faturada görünecek isim"}
                    className="rounded-sm"
                    data-testid="master-staff-invoice-title"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="label-eyebrow">
                    {staffEditForm.invoice_type === "company" ? "Vergi No" : "TC Kimlik No"}
                  </Label>
                  <Input
                    value={staffEditForm.tax_no}
                    onChange={(e) => setStaffEditForm({ ...staffEditForm, tax_no: e.target.value })}
                    className="rounded-sm font-mono"
                    data-testid="master-staff-tax-no"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="label-eyebrow">
                    Vergi Dairesi {staffEditForm.invoice_type === "company" ? "" : "(opsiyonel)"}
                  </Label>
                  <Input
                    value={staffEditForm.tax_office}
                    onChange={(e) => setStaffEditForm({ ...staffEditForm, tax_office: e.target.value })}
                    className="rounded-sm"
                    data-testid="master-staff-tax-office"
                  />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="label-eyebrow">Fatura Adresi (Adresten farklıysa)</Label>
                  <Textarea
                    rows={2}
                    value={staffEditForm.invoice_address}
                    onChange={(e) => setStaffEditForm({ ...staffEditForm, invoice_address: e.target.value })}
                    placeholder="Boş bırakılırsa yukarıdaki adres kullanılır"
                    className="rounded-sm"
                    data-testid="master-staff-invoice-address"
                  />
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setStaffEditTarget(null)} className="rounded-sm">İptal</Button>
              <Button type="submit" disabled={staffEditSaving} className="rounded-sm bg-foreground text-background hover:bg-foreground/90" data-testid="master-staff-submit">
                {staffEditSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kaydet"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="text-center">
      <div className="flex items-center justify-center gap-1 text-muted-foreground">
        <Icon className="h-3 w-3" />
        <span className="label-eyebrow">{label}</span>
      </div>
      <div className="font-display font-bold text-lg mt-1">{value || 0}</div>
    </div>
  );
}
